/**
 * File type utilities for MIME type detection and icon mapping
 */

/**
 * Guess MIME type from file extension
 * @param {string} fileName - The file name or path
 * @returns {string|null} - The MIME type or null if unknown
 */
export const getMimeTypeFromExtension = (fileName) => {
  if (!fileName) return null;

  // Check if file has an extension (contains a dot that's not at the start)
  const lastDotIndex = fileName.lastIndexOf('.');
  if (lastDotIndex <= 0 || lastDotIndex === fileName.length - 1) {
    // No extension (e.g., "README", "Dockerfile", or ends with dot)
    return null;
  }

  const ext = fileName.slice(lastDotIndex + 1).toLowerCase();
  if (!ext) return null;

  const mimeMap = {
    // Images
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
    svg: 'image/svg+xml',
    bmp: 'image/bmp',
    ico: 'image/x-icon',
    // Video
    mp4: 'video/mp4',
    webm: 'video/webm',
    mov: 'video/quicktime',
    avi: 'video/x-msvideo',
    mkv: 'video/x-matroska',
    wmv: 'video/x-ms-wmv',
    flv: 'video/x-flv',
    // Audio
    mp3: 'audio/mpeg',
    wav: 'audio/wav',
    ogg: 'audio/ogg',
    flac: 'audio/flac',
    aac: 'audio/aac',
    m4a: 'audio/mp4',
    wma: 'audio/x-ms-wma',
    // Documents
    pdf: 'application/pdf',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ppt: 'application/vnd.ms-powerpoint',
    pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    csv: 'text/csv',
    // Archives
    zip: 'application/zip',
    rar: 'application/x-rar-compressed',
    '7z': 'application/x-7z-compressed',
    tar: 'application/x-tar',
    gz: 'application/gzip',
    // Code/Text
    txt: 'text/plain',
    md: 'text/markdown',
    json: 'application/json',
    xml: 'application/xml',
    html: 'text/html',
    css: 'text/css',
    js: 'application/javascript',
  };

  return mimeMap[ext] || null;
};

/**
 * Get the appropriate icon name for a file type
 * Icon names must match those available in ui/Icon/index.js
 * @param {string} fileType - The MIME type of the file
 * @returns {string} - The icon name
 */
export const getFileIcon = (fileType) => {
  if (!fileType) return 'File';

  const type = fileType.toLowerCase();

  // Video files
  if (type.startsWith('video/')) return 'VideoFile';

  // Audio files
  if (type.startsWith('audio/')) return 'AudioFile';

  // Image files
  if (type.startsWith('image/')) return 'Image';

  // PDF documents
  if (type.includes('pdf')) return 'PDF';

  // Microsoft Word documents
  if (type.includes('msword') || type.includes('wordprocessingml') || type.includes('opendocument.text')) return 'EditNote';

  // Microsoft Excel / Spreadsheets
  if (type.includes('excel') || type.includes('spreadsheetml') || type.includes('opendocument.spreadsheet') || type.includes('csv')) return 'List';

  // Microsoft PowerPoint / Presentations
  if (type.includes('powerpoint') || type.includes('presentationml') || type.includes('opendocument.presentation')) return 'ViewModule';

  // Archive/Compressed files
  if (type.includes('zip') || type.includes('rar') || type.includes('7z') || type.includes('tar') || type.includes('gzip') || type.includes('compressed'))
    return 'Archive';

  // Code/Text files
  if (
    type.includes('javascript') ||
    type.includes('json') ||
    type.includes('xml') ||
    type.includes('html') ||
    type.includes('css') ||
    type.includes('text/plain') ||
    type.includes('text/markdown')
  )
    return 'Notes';

  // Default
  return 'File';
};

/**
 * Format file size in human-readable format
 * @param {number} bytes - File size in bytes
 * @returns {string} - Formatted file size (e.g., "1.5 MB", "2.3 GB")
 */
export const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  return (bytes / (1024 * 1024 * 1024)).toFixed(1) + ' GB';
};

/**
 * Validate if a string is a valid URL
 * @param {string} string - The string to validate
 * @returns {boolean} - True if valid URL
 */
export const isValidUrl = (string) => {
  try {
    new URL(string);
    return true;
  } catch {
    return false;
  }
};

/**
 * Check if a file's type is accepted based on an accept string
 * @param {File} file - The file to check
 * @param {string} acceptString - Accept string (e.g., "image/*,.pdf")
 * @returns {boolean} - True if file type is accepted
 */
export const isFileTypeAccepted = (file, acceptString) => {
  if (!acceptString) return true;

  const acceptedTypes = acceptString.split(',').map((type) => type.trim().toLowerCase());
  const fileType = file.type.toLowerCase();
  const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();

  return acceptedTypes.some((accepted) => {
    if (accepted === fileType) return true;
    if (accepted.startsWith('.') && accepted === fileExtension) return true;
    if (accepted.endsWith('/*')) {
      const baseType = accepted.slice(0, -2);
      if (fileType.startsWith(baseType)) return true;
    }
    return false;
  });
};

/**
 * Format accepted file types for display
 * @param {string} acceptString - Accept string (e.g., "image/*,.pdf")
 * @returns {string|null} - Formatted string (e.g., "Images, PDF")
 */
export const formatAcceptedTypes = (acceptString) => {
  if (!acceptString) return null;

  const types = acceptString.split(',').map((type) => {
    const trimmed = type.trim();
    if (trimmed.startsWith('.')) return trimmed.slice(1).toUpperCase();
    if (trimmed === 'image/*') return 'Images';
    if (trimmed === 'video/*') return 'Videos';
    if (trimmed === 'audio/*') return 'Audio';
    if (trimmed.includes('/')) return trimmed.split('/')[1].toUpperCase();
    return trimmed.toUpperCase();
  });

  return [...new Set(types)].join(', ');
};

/**
 * Check if a MIME type is accepted based on an accept string
 * Similar to isFileTypeAccepted but works with MIME type strings instead of File objects
 * Used for validating URLs where we only have the detected MIME type
 * @param {string} mimeType - The MIME type to check (e.g., "image/jpeg")
 * @param {string} acceptString - Accept string (e.g., "image/*,.pdf")
 * @returns {boolean} - True if MIME type is accepted
 */
export const isUrlTypeAccepted = (mimeType, acceptString) => {
  if (!acceptString || !mimeType) return true;

  const acceptedTypes = acceptString.split(',').map((type) => type.trim().toLowerCase());
  const lowerMimeType = mimeType.toLowerCase();

  return acceptedTypes.some((accepted) => {
    // Exact MIME type match
    if (accepted === lowerMimeType) return true;
    // Wildcard match (e.g., "image/*" matches "image/jpeg")
    if (accepted.endsWith('/*')) {
      const baseType = accepted.slice(0, -2);
      if (lowerMimeType.startsWith(baseType + '/')) return true;
    }
    // Extension match - extract extension from MIME type if possible
    if (accepted.startsWith('.')) {
      // Map common MIME types to extensions for comparison
      const extFromMime = getExtensionFromMimeType(lowerMimeType);
      if (extFromMime && accepted === '.' + extFromMime) return true;
    }
    return false;
  });
};

/**
 * Get file extension from MIME type
 * @param {string} mimeType - The MIME type
 * @returns {string|null} - The file extension without dot, or null if unknown
 */
const getExtensionFromMimeType = (mimeType) => {
  const mimeToExt = {
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/gif': 'gif',
    'image/webp': 'webp',
    'image/svg+xml': 'svg',
    'image/bmp': 'bmp',
    'video/mp4': 'mp4',
    'video/webm': 'webm',
    'video/quicktime': 'mov',
    'audio/mpeg': 'mp3',
    'audio/wav': 'wav',
    'audio/ogg': 'ogg',
    'application/pdf': 'pdf',
    'application/zip': 'zip',
    'text/plain': 'txt',
    'text/csv': 'csv',
    'application/json': 'json',
  };
  return mimeToExt[mimeType] || null;
};
